import { refreshTokens } from '@/api/auth';
import { useFeatureFlags } from '@/configs';
import {
  clearTokens,
  getRefreshToken,
  isAccessTokenValid,
  isRefreshTokenValid,
  saveTokens,
} from '@/utils/token-storage';
import { MiniKit } from '@worldcoin/minikit-js';
import { useEffect } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/shallow';
import { CONSTANT } from './constants';

export const KEY_AUTH = '@app/holdDefurure/user';

interface AuthStore {
  loading: boolean;
  address: string;
  username: string;
  profile_picture_url?: string | null;
}

type Actions = {
  setLoading: (isLoading: boolean) => void;
  setAddress: (address: string) => void;
  setUserInfo: (info: {
    address: string;
    username: string;
    profile_picture_url?: string | null;
  }) => void;
};

export const useAuth = create<AuthStore & Actions>()(
  immer((set) => ({
    loading: false,
    address: localStorage.getItem(KEY_AUTH) || '',
    username: '',
    setLoading: (isLoading) =>
      set((state) => {
        state.loading = isLoading;
      }),
    setAddress: (address) =>
      set((state) => {
        state.address = address;
      }),

    setUserInfo: (info) => {
      set({ ...info, loading: false });
    },
  }))
);

export interface UserResponse {
  username: string;
  address: string;
  profile_picture_url: null;
}

export function useAutoLogin() {
  const { siweAuth } = useFeatureFlags();
  const [setLoading, setUserInfo, setAddress] = useAuth(
    useShallow((state) => [
      state.setLoading,
      state.setUserInfo,
      state.setAddress,
    ])
  );

  useEffect(() => {
    async function fetchUser() {
      const address = localStorage.getItem(KEY_AUTH);
      if (!address) {
        return;
      }

      try {
        setLoading(true);

        if (siweAuth && !isAccessTokenValid()) {
          if (!isRefreshTokenValid()) {
            throw new Error('session expired');
          }
          const tokens = await refreshTokens(getRefreshToken()!);
          saveTokens(tokens);
        }

        const data = await MiniKit.getUserInfo(address);

        setUserInfo({
          address: data.walletAddress,
          profile_picture_url:
            data.profilePictureUrl ?? CONSTANT.DEFAULT_AVATAR,
          username: data.username ?? '',
        });
      } catch (error) {
        console.debug('Failed to restore session', error);
        if (siweAuth) {
          clearTokens();
          localStorage.removeItem(KEY_AUTH);
          setAddress('');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [setLoading, setUserInfo, setAddress, siweAuth]);
}
